import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import type { ReactNode } from "react";
import {
  ArrowUpRight,
  Check,
  ChevronRight,
  CircleHelp,
  FileText,
  LoaderCircle,
  LockKeyhole,
  Search,
  ShieldCheck,
  X,
} from "lucide-react";
export function Badge({
  children,
  strong = false,
}: {
  children: ReactNode;
  strong?: boolean;
}) {
  return (
    <span className={"badge" + (strong ? " strong" : "")}>
      <span />
      {children}
    </span>
  );
}
export function Button({
  children,
  onClick,
  variant = "",
  disabled = false,
  type = "button",
  title,
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: string;
  disabled?: boolean;
  type?: "button" | "submit";
  title?: string;
}) {
  return (
    <button
      type={type}
      className={"btn " + variant}
      onClick={onClick}
      disabled={disabled}
      title={title}
    >
      {children}
    </button>
  );
}
export function Empty({
  title,
  description,
  action,
  icon,
}: {
  title: string;
  description: string;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="empty">
      <div className="empty-symbol">{icon || <FileText size={23} />}</div>
      <h3>{title}</h3>
      <p>{description}</p>
      {action}
    </div>
  );
}
export function Skeleton() {
  return (
    <div
      className="skeleton-stack"
      aria-label="Loading workspace"
      role="status"
    >
      <div className="skeleton heading" />
      <div className="skeleton cards" />
      <div className="skeleton table" />
    </div>
  );
}
export function Callout({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="callout">
      <LockKeyhole size={17} />
      <div>
        <strong>{title}</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}
export function SectionTitle({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="section-title">
      <div>
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {action}
    </div>
  );
}
const modalStack: HTMLElement[] = [];
let modalBodyOverflow = "";
export function Modal({
  title,
  children,
  close,
  wide = false,
}: {
  title: string;
  children: ReactNode;
  close: () => void;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement;
    const el = ref.current;
    if (!el) return;
    if (!modalStack.length) modalBodyOverflow = document.body.style.overflow;
    modalStack.push(el);
    const initialFocus =
      el.querySelector<HTMLElement>(
        'input:not(:disabled):not([type="checkbox"]),textarea:not(:disabled),select:not(:disabled)',
      ) || el;
    initialFocus.focus();
    const key = (e: KeyboardEvent) => {
      if (modalStack.at(-1) !== el) return;
      if (e.key === "Escape") close();
      if (e.key === "Tab") {
        const nodes = [
          ...(el?.querySelectorAll<HTMLElement>(
            'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], [tabindex="0"]',
          ) || []),
        ].filter((node) => node.getClientRects().length > 0);
        const first = nodes[0],
          last = nodes.at(-1);
        if (!first) {
          e.preventDefault();
          return;
        }
        if (
          e.shiftKey &&
          (document.activeElement === first || document.activeElement === el)
        ) {
          e.preventDefault();
          last?.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", key);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", key);
      const index = modalStack.indexOf(el);
      if (index >= 0) modalStack.splice(index, 1);
      if (!modalStack.length) document.body.style.overflow = modalBodyOverflow;
      if (previous?.isConnected) previous.focus();
    };
  }, [close]);
  const content = (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        className={"modal " + (wide ? "wide" : "")}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={ref}
        tabIndex={-1}
      >
        <div className="modal-head">
          <h2>{title}</h2>
          <button
            className="icon-btn"
            aria-label="Close dialog"
            onClick={close}
          >
            <X size={19} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
  return typeof document === "undefined"
    ? content
    : createPortal(content, document.body);
}
export function KeyValue({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="key-value">
      <span>{label}</span>
      <strong>{children}</strong>
    </div>
  );
}
export function MiniIcon({ type = "document" }: { type?: string }) {
  return (
    <span className="mini-icon">
      {type === "shield" ? (
        <ShieldCheck size={19} />
      ) : type === "search" ? (
        <Search size={19} />
      ) : (
        <FileText size={19} />
      )}
    </span>
  );
}
export function Progress({ value, label }: { value: number; label: string }) {
  return (
    <div
      className="progress"
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      <span style={{ width: value + "%" }} />
    </div>
  );
}
